import { Box } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { addAppReserve } from '../../../../actions/appReserves/AppReservesActions';
import { parsePhone, formatPhoneReadable } from '../../../../helpers/helpers';
import Reserve from '../../../../types/IAppReservesStore';
import * as Booking from '../../../entities/booking';
import { useGetBookingRequestRange } from '../../../entities/reservesSchema/lib/useGetBookingRequestRange';
import { ReactComponent as CommentIcon } from '../../../shared/assets/icons/comment.svg';
import { ReactComponent as GlassesIcon } from '../../../shared/assets/icons/glasses.svg';
import { ReactComponent as LightningIcon } from '../../../shared/assets/icons/lightning.svg';
import { toTzMoment } from '../../../shared/lib';
import { getReservePersonName, isTableOccupied, getReserveItemState, tableColors } from '../lib';
import { lightTheme } from '../lib/theme';
import { Duo } from './duo';
import { Modal } from './modal';
import { ReserveFooterWidget } from './reserveFooter';
import { ReserveInfo } from './reserveInfo';
import { SimpleButton } from './simpleButton';
import { SolidButton } from './solidButton';
import { SvgIcon } from './svgIcon';
import { Tag } from './tag';

const { fg } = lightTheme;
const { badge } = lightTheme.components;

export const OpenedReserveWidget = ({
  scheduleReserve,
  onCollapse,
  onCancel,
  isCancelDisabled = false,
  tablesSchedule,
  // isTableView,
  forcedOpen = false,
  restaurantTz,
  // compact = false,
}: {
  scheduleReserve: GM.ReservesSchemas.ScheduleReserve;
  onCollapse: () => void;
  onCancel?: () => void;
  isCancelDisabled?: boolean;
  tablesSchedule: GM.ReservesSchemas.TablesSchedule;
  // isTableView: boolean;
  forcedOpen?: boolean;
  restaurantTz?: string;
  // compact?: boolean;
}) => {
  const [isMergeConfirmation, setIsMergeConfirmation] = useState<boolean>(false);

  const dispatch = useDispatch();
  const person = getReservePersonName(scheduleReserve);
  const date = toTzMoment(scheduleReserve.date, restaurantTz);
  const timeStr = date.format('HH:mm');
  const state = getReserveItemState(
    scheduleReserve,
    tablesSchedule[scheduleReserve.tables[0].id],
    restaurantTz,
  );

  const { from } = useGetBookingRequestRange(date.valueOf());
  const guestArrived = Booking.useGuestArrivedMutation(from);
  const mergeReserveWithOrder = Booking.useMergeReserveWithOrderMutation(from);

  useEffect(() => {
    if (mergeReserveWithOrder.isError || mergeReserveWithOrder.isSuccess) {
      setIsMergeConfirmation(null);
    }
  }, [mergeReserveWithOrder.isSuccess, mergeReserveWithOrder.isError]);

  // const phone = scheduleReserve.phone && parsePhone(scheduleReserve.phone);
  let phone: string | null = null;
  if (scheduleReserve.phone) {
    const phoneParsed = parsePhone(scheduleReserve.phone);
    if (phoneParsed) {
      phone = formatPhoneReadable(phoneParsed);
    }
  }

  let ableToMergeWithOrderId: number | undefined;

  scheduleReserve.tables.forEach((table) => {
    const currentScheduleItem = tablesSchedule[table.id]?.current;
    if (!currentScheduleItem) {
      return;
    }

    if (currentScheduleItem.id !== scheduleReserve.id && isTableOccupied(currentScheduleItem)) {
      if (
        currentScheduleItem.scheduleType === 'ORDER' ||
        !currentScheduleItem.isScheduled
        // !currentScheduleItem.isBanquet
      ) {
        ableToMergeWithOrderId = currentScheduleItem.id;
      }
    }
  });

  let currentTableScheduleItem:
    | GM.ReservesSchemas.ScheduleReserve
    | GM.ReservesSchemas.ScheduleOrder
    | undefined;
  for (const t of scheduleReserve.tables) {
    const tData = tablesSchedule[t.id];
    if (tData?.current) {
      currentTableScheduleItem = tData.current;
      break;
    }
  }

  const isAbilityToOccupy = !currentTableScheduleItem || !isTableOccupied(currentTableScheduleItem);
  const isWarningMessage = ['DANGER', 'WARNING', 'CONFLICT'].includes(state);
  const isPrepay = !!scheduleReserve.prepay?.amount;

  const renderPrepayTag = () => {
    if (!isPrepay) {
      return null;
    }

    const isPaid = scheduleReserve.state !== 'AWAIT_PREPAY';

    const stateColorName = !isPaid && !isWarningMessage ? 'WARNING' : state;
    const tagStyles: React.CSSProperties = {
      color: tableColors[stateColorName].head.color,
      background: tableColors[stateColorName].head.bg,
    };

    if (tableColors[stateColorName].head.stripe) {
      tagStyles['background'] = `repeating-linear-gradient(
            -45deg,
            ${tableColors[stateColorName].head.stripe} 0 4px,
            transparent 4px 8px
          ), ${tagStyles.background}`;
    }

    return (
      <Tag style={{ ...tagStyles, padding: '2px 9px' }}>
        Депозит
        {isPaid ? ' ' : ' не '}
        оплачен: {scheduleReserve.prepay.amount} ₽
      </Tag>
    );
  };

  return (
    <>
      {isMergeConfirmation && (
        <Modal
          open
          cancelButtonLabel="Отмена"
          onClose={() => setIsMergeConfirmation(false)}
          confirmButtonLabel="Да, гости на месте"
          onConfirm={() => {
            mergeReserveWithOrder.mutate({
              reserveId: scheduleReserve.id,
              orderId: ableToMergeWithOrderId,
            });
          }}
        >
          <Box>Подтвердите, что гости по&nbsp;этому резерву уже сидят за&nbsp;столом:</Box>

          <ReserveInfo reserve={scheduleReserve} restaurantTz={restaurantTz} state={state} />
        </Modal>
      )}

      <Box className="design-v3-font" onClick={forcedOpen ? undefined : () => onCollapse()}>
        <Box mb="8px">
          <Box mb="4px">
            <Tag
              style={{
                color: tableColors[state].head.color,
                background: tableColors[state].head.stripe
                  ? `repeating-linear-gradient(
                      -45deg,
                      ${tableColors[state].head.stripe} 0 4px,
                      transparent 4px 8px
                    ), ${tableColors[state].head.bg}`
                  : tableColors[state].head.bg,
                padding: '2px 9px',
              }}
            >
              <span style={{ fontWeight: 600 }}>{timeStr}</span>{' '}
              <span style={{ fontWeight: 400 }}>{date.format('DD.MM')}</span>
            </Tag>
          </Box>

          {isPrepay && <Box mb="4px">{renderPrepayTag()}</Box>}

          {['WARNING', 'CONFLICT'].includes(state) && (
            <Box
              mb="4px"
              color={tableColors[state].head.color}
              bgcolor={tableColors[state].head.bg}
              borderRadius={8}
              padding="8px 12px"
              fontSize={14}
            >
              {state === 'WARNING' && <>На этот стол скоро должен прийти гость</>}
              {state === 'CONFLICT' && (
                <>Время резерва наступило, но&nbsp;за&nbsp;столом сидят другие гости</>
              )}
            </Box>
          )}

          {scheduleReserve.isAutoBooking && (
            <Tag
              style={{
                color: fg.default,
                backgroundColor: badge.muted.neutral.bg,
                marginRight: '4px',
                marginBottom: '4px',
                padding: '2px 9px',
              }}
            >
              <Duo
                first={<SvgIcon icon={LightningIcon} width={10} height={14} />}
                second={<span style={{ fontSize: 12, fontWeight: 500 }}>Мгновенный резерв</span>}
                gridColumnGap={4}
                alignItems="center"
              />
            </Tag>
          )}

          {scheduleReserve.isBanquet && (
            <Tag
              style={{
                color: fg.default,
                backgroundColor: badge.muted.neutral.bg,
                marginRight: '4px',
                marginBottom: '4px',
                padding: '2px 9px',
              }}
            >
              <Duo
                first={<SvgIcon icon={GlassesIcon} width={14} height={14} />}
                second={<span style={{ fontSize: 12, fontWeight: 500 }}>Банкет</span>}
                gridColumnGap={4}
                alignItems="center"
              />
            </Tag>
          )}
        </Box>

        {person && (
          <Box
            fontSize={16}
            fontWeight={600}
            color={fg.default}
            marginBottom="16px"
            style={{ lineHeight: 1.3 }}
          >
            {person}
          </Box>
        )}

        <Box display="flex" flexDirection="row" gridColumnGap={8} mb="8px">
          <Box flexGrow={1} width="50%">
            <SimpleButton
              style={{
                width: '100%',
                fontSize: 16,
                padding: '10px 22px',
              }}
              onClick={() => {
                // Открытие редактирования резерва по аналогии
                //   с handleOpenEditReserveModal
                //   в src/containers/Booking/Booking.tsx
                dispatch(addAppReserve(new Reserve('APP', scheduleReserve)));
              }}
              label="Изменить"
            />
          </Box>
          <Box flexGrow={1} width="50%">
            <SimpleButton
              style={{
                width: '100%',
                fontSize: 16,
                padding: '10px 22px',
              }}
              onClick={onCancel}
              label="Отменить"
              disabled={!onCancel || isCancelDisabled}
            />
          </Box>
        </Box>

        {isAbilityToOccupy && (
          <SolidButton
            onClick={() => guestArrived.mutate({ reserveId: scheduleReserve.id })}
            label="Гость пришел"
            style={{
              width: '100%',
              fontSize: 16,
              padding: '10px 22px',
            }}
          />
        )}

        {ableToMergeWithOrderId && (
          <SolidButton
            onClick={() => setIsMergeConfirmation(true)}
            label="Гости на месте"
            style={{
              width: '100%',
              fontSize: 16,
              padding: '10px 22px',
            }}
          />
        )}

        {(phone || scheduleReserve.comment) && (
          <Box mt="16px">
            {phone && (
              <Box
                fontSize={14}
                mb={scheduleReserve.comment ? '16px' : 0}
                fontWeight={400}
                color={fg.default}
              >
                {phone}
              </Box>
            )}

            {scheduleReserve.comment && (
              <Duo
                first={<SvgIcon icon={CommentIcon} width={14} height={14} />}
                second={
                  <Box lineHeight={1.2} fontSize={14} marginTop="-2px" color={fg.muted}>
                    {scheduleReserve.comment}
                  </Box>
                }
                gridColumnGap={8}
              />
            )}
          </Box>
        )}

        <ReserveFooterWidget
          room={scheduleReserve.tables[0].hall}
          tables={scheduleReserve.tables.map((t) => t.number)}
          persons={scheduleReserve.persons}
          onClickCollapse={(nextCollapsed) => {
            if (!nextCollapsed) {
              onCollapse();
            }
          }}
          // compact={compact}
          // isTableView={isTableView}
          forcedOpen={forcedOpen}
        />
      </Box>
    </>
  );
};
